// frontend/src/pages/ESIMManagement.js
import React, { useState, useCallback } from "react";
import { Box, Grid, Typography, Alert } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ESIMPackages from "../components/ESIMPackages/ESIMPackages";
import { esimAPI } from "../services/api";

const ESIMManagement = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  // Use useCallback to memoize the fetchPackages function
  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await esimAPI.getPackages();
      setPackages(response.data.obj?.packageList || []);
    } catch (err) {
      console.error("Error fetching packages:", err);
      setError(err.response?.data?.message || t("errors.fetchPackagesFailed"));
      enqueueSnackbar(t("errors.fetchPackagesFailed"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [t, enqueueSnackbar]); // Include dependencies

  const handlePurchase = async (purchaseData) => {
    try {
      setPurchaseLoading(true);
      const response = await esimAPI.purchase(purchaseData);

      if (response.data.success) {
        const orderStatus = response.data.order.status;

        enqueueSnackbar(
          orderStatus === "pending"
            ? t("esim.purchase.success.pending")
            : t("esim.purchase.success.ready"),
          { variant: "success.success" }
        );

        // Refresh packages list after successful purchase
        await fetchPackages();
        return true;
      } else {
        throw new Error(
          response.data.message || t("esim.purchase.error.generic")
        );
      }
    } catch (err) {
      console.error("Purchase error:", err);
      const errorMessage =
        err.response?.data?.message === "Insufficient credits"
          ? t("esim.purchase.error.insufficientCredits")
          : err.response?.data?.message || t("esim.purchase.error.generic");

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 5000,
      });
      return false;
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Use useEffect with the memoized fetchPackages
  React.useEffect(() => {
    fetchPackages();
  }, [fetchPackages]); // Include fetchPackages as dependency

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("esim.management.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ESIMPackages
            packages={packages}
            loading={loading}
            purchaseLoading={purchaseLoading}
            onRefresh={fetchPackages}
            onPurchase={handlePurchase}
            setLoading={setLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ESIMManagement;
